/**
 * Diccionario base de las pantallas de autenticación. Es el contrato: el tipo
 * `AuthDictionary` sale de aquí, así que cualquier clave nueva obliga a
 * traducirla en `auth.en.ts` para que compile.
 */
export const AUTH_ES = {
  // --- Compartido -----------------------------------------------------------
  'common.continue': 'Continuar',
  'common.previous': 'Anterior',
  'common.cancel': 'Cancelar',
  'common.back': 'Volver',
  'common.required': 'Este campo es obligatorio',
  'common.optional': '(opcional)',
  'common.emailInvalid': 'Ingresa un correo electrónico válido',
  'common.showPassword': 'Mostrar contraseña',
  'common.hidePassword': 'Ocultar contraseña',
  'common.haveAccount': '¿Ya tienes cuenta?',
  'common.signInLink': 'Iniciar sesión →',

  // --- Roles (terminología dexCarga) ----------------------------------------
  'role.broker': 'Embarcador',
  'role.shipper': 'Generador de carga',
  'role.carrier': 'Transportista',

  // --- Login ----------------------------------------------------------------
  'login.title': 'Iniciar sesión',
  'login.subtitle': 'Descubre una forma más eficiente de operar tus embarques',
  'login.email': 'Correo electrónico',
  'login.password': 'Contraseña',
  'login.remember': 'Recuérdame',
  'login.forgot': 'Olvidé mi contraseña',
  'login.submit': 'Iniciar sesión',
  'login.noAccount': '¿No tienes cuenta?',
  'login.createAccount': 'Crear cuenta →',

  // --- Recuperar contraseña -------------------------------------------------
  'forgot.title': 'Recuperar contraseña',
  'forgot.subtitle': 'Te enviaremos un enlace seguro a tu correo para restablecerla.',
  'forgot.step.email': 'Correo electrónico',
  'forgot.step.emailHint': 'Recibe el enlace',
  'forgot.step.reset': 'Restablecimiento',
  'forgot.step.resetHint': 'Actualiza tu acceso',
  'forgot.sent.title': 'Enlace enviado',
  'forgot.sent.body': 'Revisa tu bandeja. Enviamos las instrucciones a:',
  'forgot.backToLogin': 'Volver al inicio de sesión',
  'forgot.email': 'Correo electrónico',
  'forgot.hint': 'El enlace solo se enviará por correo electrónico. No se solicitará verificación por SMS.',
  'forgot.sending': 'Enviando…',
  'forgot.send': 'Enviar enlace',
  'forgot.failed': 'No pudimos enviar el enlace',

  // --- Registro: selección de rol -------------------------------------------
  'registry.role.title': 'Elige tu tipo de usuario',
  'registry.role.subtitle': 'Selecciona el perfil con el que crearás tu cuenta.',
  'registry.role.label': 'Tipo de usuario',

  // --- Registro: cabecera y stepper -----------------------------------------
  'registry.title': 'Crear tu cuenta',
  'registry.subtitle': 'Tres pasos rápidos para activar tu panel.',
  'registry.step.personal': 'Datos personales',
  'registry.step.personalHint': 'Tu información de acceso',
  'registry.step.company': 'Información de la empresa',
  'registry.step.companyHint': 'Datos fiscales y domicilio',
  'registry.step.verification': 'Verificación',
  'registry.step.verificationHint': 'Confirma tu identidad',

  // --- Registro: datos personales -------------------------------------------
  'registry.personal.firstName': 'Nombre',
  'registry.personal.lastName': 'Apellido',
  'registry.personal.email': 'Correo electrónico',
  'registry.personal.phone': 'Teléfono',
  'registry.personal.phoneInvalid': 'Ingresa un teléfono válido (7 a 15 dígitos)',
  'registry.personal.countryCode': 'Código de país',
  'registry.personal.countryCodeSearch': 'Buscar país o código...',
  'registry.personal.password': 'Contraseña',
  'registry.personal.confirmPassword': 'Repetir contraseña',
  'registry.password.title': 'La contraseña debe cumplir:',
  'registry.password.length': 'Mínimo 8 caracteres',
  'registry.password.upper': 'Al menos una letra mayúscula',
  'registry.password.lower': 'Al menos una letra minúscula',
  'registry.password.digit': 'Al menos un número (0-9)',
  'registry.password.symbol': 'Al menos un símbolo',
  'registry.password.match': 'Las contraseñas deben ser iguales',

  // --- Registro: empresa ----------------------------------------------------
  'registry.company.section': 'Datos de la empresa',
  'registry.company.rfc': 'RFC',
  'registry.company.validate': 'Validar',
  'registry.company.rfcAvailable': 'RFC disponible',
  'registry.company.rfcExisting': 'Empresa existente; solicitaremos acceso al administrador.',
  'registry.company.rfcValidateFirst': 'Valida el RFC antes de continuar',
  'registry.company.rfcFormatValid': 'Formato de RFC válido',
  'registry.company.rfcFormatInvalid': 'Formato de RFC inválido',
  'registry.company.legalName': 'Razón social',
  'registry.company.legalNamePlaceholder': 'Nombre de tu empresa',
  'registry.company.taxRegime': 'Régimen fiscal',
  'registry.company.taxRegimePlaceholder': 'Selecciona régimen...',
  'registry.company.newCompanyHint': 'Ingresa los datos para registrar una nueva empresa.',
  'registry.company.privacyNote': 'Ya contamos con el domicilio fiscal de esta empresa. Por privacidad no se muestra ni es necesario capturarlo.',

  // --- Registro: domicilio fiscal -------------------------------------------
  'registry.address.section': 'Domicilio fiscal',
  'registry.address.zipcode': 'Código postal',
  'registry.address.zipcodePlaceholder': 'Escribe tu CP (5 dígitos)',
  'registry.address.zipcodeHint': 'Elige tu colonia y se completará el resto.',
  'registry.address.zipcodeNotFound': 'CP no encontrado en el catálogo SAT',
  'registry.address.catalogUnavailable': 'No pudimos consultar el catálogo SAT; captura los datos manualmente.',
  'registry.address.state': 'Estado',
  'registry.address.city': 'Municipio / Ciudad',
  'registry.address.neighborhood': 'Colonia',
  'registry.address.street': 'Dirección',
  'registry.address.exteriorNumber': 'No. ext.',
  'registry.address.interiorNumber': 'No. int.',
  'registry.address.selectPlaceholder': 'Selecciona...',
  'registry.address.zipcodeFirst': 'Ingresa tu CP primero',
  'registry.address.manualEntry': 'El catálogo SAT no tiene este dato para tu CP; captúralo manualmente.',
  'registry.company.caat': 'CAAT',

  // --- Registro: verificación y alta ----------------------------------------
  'registry.verification.intro': 'Enviaremos un enlace de confirmación para verificar tu identidad y activar la cuenta.',
  'registry.verification.boxTitle': 'Verificación por correo',
  'registry.verification.boxBody': 'Al crear tu cuenta enviaremos un enlace de confirmación a:',
  'registry.verification.boxFooter': 'Deberás abrir el enlace para activar tu cuenta antes de iniciar sesión.',
  'registry.verification.submit': 'Crear cuenta',
  'registry.signup.pendingTitle': 'Estamos creando tu cuenta',
  'registry.signup.pendingBody': 'Por favor espera un momento…',
  'registry.signup.successTitle': '¡Registro completado!',
  'registry.signup.successBody': 'Revisa tu correo para confirmar tu cuenta; si la empresa existe, te notificaremos cuando sea aprobada.',
  'registry.signup.successCta': 'Volver al inicio',
  'registry.signup.errorTitle': 'No pudimos completar el registro',
  'registry.signup.errorCta': 'Regresar',

  // --- Registro: avisos (toasts) --------------------------------------------
  'toast.personalIncomplete': 'Completa tus datos personales',
  'toast.passwordWeak': 'La contraseña no cumple los requisitos',
  'toast.rfcInvalid': 'Ingresa un RFC válido',
  'toast.rfcValidateFirst': 'Valida el RFC antes de continuar',
  'toast.companyIncomplete': 'Completa la información de la empresa',
  'toast.zipcodeInvalid': 'El código postal debe tener 5 dígitos',
  'toast.addressIncomplete': 'Completa el domicilio fiscal',
  'toast.rfcAvailable': 'RFC disponible',
  'toast.rfcExisting': 'Empresa existente; solicitaremos acceso al administrador.',
  'toast.legalAccepted': 'Términos y aviso de privacidad aceptados',
  'toast.signupFailed': 'No pudimos completar el registro',

  // --- Avisos del inicio de sesión ------------------------------------------
  'toast.loginFailed': 'No pudimos iniciar sesión',
  'toast.roleNotAuthorized': 'Este perfil no puede iniciar sesión aquí',
  'toast.roleNotAuthorizedDetail': 'Las cuentas de operador se usan desde la app móvil.',

  // --- Consentimiento legal -------------------------------------------------
  'legal.terms.eyebrow': 'Paso 1 de 2',
  'legal.terms.title': 'Términos de servicio',
  'legal.terms.description': 'Antes de continuar necesitamos que revises y aceptes los términos que rigen el uso de dexCarga.',
  'legal.terms.link': 'Leer los Términos de servicio',
  'legal.terms.checkbox': 'He leído y acepto los Términos de servicio.',
  'legal.terms.point1': 'Cómo puedes usar la plataforma y las cuentas de tu empresa.',
  'legal.terms.point2': 'Responsabilidades de cada parte en la coordinación de embarques.',
  'legal.terms.point3': 'Condiciones de contratación, vigencia y cancelación.',
  'legal.privacy.eyebrow': 'Paso 2 de 2',
  'legal.privacy.title': 'Aviso de privacidad',
  'legal.privacy.description': 'También necesitamos tu consentimiento sobre el tratamiento de tus datos personales.',
  'legal.privacy.link': 'Leer el Aviso de privacidad',
  'legal.privacy.checkbox': 'He leído y acepto el Aviso de privacidad.',
  'legal.privacy.point1': 'Qué datos personales y fiscales recabamos durante el registro.',
  'legal.privacy.point2': 'Para qué los usamos y con quién podemos compartirlos.',
  'legal.privacy.point3': 'Cómo ejercer tus derechos ARCO sobre tu información.',
  'legal.privacy.point4': 'Cómo registramos el uso que haces de la plataforma —pantallas, clics y fallos, nunca lo que escribes— para darte soporte y mejorarla, por cuánto tiempo, y cómo oponerte.',
  'legal.acceptAndContinue': 'Aceptar y continuar',
  'legal.acceptAndFinish': 'Acepto y finalizo',
  'legal.close': 'Cerrar',

  // --- Selector de idioma ---------------------------------------------------
  'lang.es': 'México - Español',
  'lang.en': 'USA - English',
  'lang.label': 'Idioma',
} as const;
